import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { IndianRupee, CheckCircle2, Clock, FileText, AlertCircle } from 'lucide-react';

const API = '/api';

const STATUS_CONFIG = {
  paid: { label: 'Paid', color: 'text-emerald-600', bg: 'bg-emerald-50', icon: CheckCircle2 },
  invoiced: { label: 'Invoiced', color: 'text-blue-600', bg: 'bg-blue-50', icon: FileText },
  pending: { label: 'Pending', color: 'text-stone-400', bg: 'bg-stone-50', icon: Clock },
};

export default function PaymentTracker({ worksheetId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!worksheetId) return;
    const fetch = async () => {
      try {
        const res = await axios.get(`${API}/boq-worksheets/${worksheetId}/payment-tracker`);
        setData(res.data);
      } catch { /* no tracker data */ }
      setLoading(false);
    };
    fetch();
  }, [worksheetId]);

  if (loading) return <div className="text-sm text-stone-400 py-3">Loading tracker...</div>;
  if (!data || !data.tracker || data.tracker.length === 0) return null;

  const paidPct = data.total_value > 0 ? (data.total_paid / data.total_value) * 100 : 0;
  const invoicedPct = data.total_value > 0 ? (data.total_invoiced / data.total_value) * 100 : 0;

  return (
    <div className="border border-stone-200 rounded-xl bg-white" data-testid="payment-tracker">
      <div className="px-4 py-3 border-b border-stone-100 bg-stone-50 rounded-t-xl">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-stone-800 flex items-center gap-2">
            <IndianRupee size={16} className="text-emerald-600" />
            Payment Tracker
          </h3>
          <div className="flex gap-3 text-xs">
            <span className="text-emerald-600 font-medium">Paid: Rs.{data.total_paid?.toLocaleString()}</span>
            <span className="text-stone-400">|</span>
            <span className="text-stone-500">Outstanding: Rs.{data.total_outstanding?.toLocaleString()}</span>
          </div>
        </div>
        {/* Progress bar */}
        <div className="mt-2 h-2 bg-stone-200 rounded-full overflow-hidden flex">
          <div className="h-full bg-emerald-500 transition-all" style={{ width: `${paidPct}%` }} />
          <div className="h-full bg-blue-400 transition-all" style={{ width: `${Math.max(0, invoicedPct - paidPct)}%` }} />
        </div>
        <div className="flex gap-4 mt-1 text-[10px] text-stone-400">
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-500 inline-block" /> Paid ({paidPct.toFixed(0)}%)</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-blue-400 inline-block" /> Invoiced</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-stone-200 inline-block" /> Pending</span>
        </div>
      </div>

      <div className="divide-y divide-stone-100">
        {data.tracker.map((item, idx) => {
          const cfg = STATUS_CONFIG[item.status] || STATUS_CONFIG.pending;
          const Icon = cfg.icon;
          return (
            <div key={idx} className="flex items-center justify-between px-4 py-2.5 hover:bg-stone-50/50 transition-colors" data-testid={`tracker-row-${idx}`}>
              <div className="flex items-center gap-3">
                <div className={`p-1.5 rounded-lg ${cfg.bg}`}>
                  <Icon size={14} className={cfg.color} />
                </div>
                <div>
                  <p className="text-sm font-medium text-stone-700">{item.milestone_name}</p>
                  <p className="text-xs text-stone-400">{item.percentage}% &middot; {item.invoice_number || 'No invoice'}</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-sm font-semibold text-stone-800">Rs.{item.amount?.toLocaleString()}</p>
                <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full ${cfg.bg} ${cfg.color}`}>
                  {cfg.label}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
